/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Admin API hooks for system administrators
 * Calls Python API endpoints at /api/admin/*
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import { useCallback, useEffect, useRef, useState } from "react";

// Get API base URL from environment
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const REST_API_BASE_URL = API_ENDPOINT.replace(/\/graphql$/, "");

// =====================================
// Admin API Types (Python Backend)
// =====================================

/** テナント情報（Admin用） */
export interface AdminTenant {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

/** テナント一覧レスポンス */
export interface AdminTenantListResponse {
  items: AdminTenant[];
  total: number;
}

/** テナントメンバー情報 */
export interface AdminTenantMember {
  userId: string;
  email: string;
  familyName: string;
  givenName: string;
  role: string;
}

/** テナント詳細レスポンス */
export interface AdminTenantDetailResponse {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  licenses: string[];
  members: AdminTenantMember[];
}

/** テナント更新リクエスト */
export interface UpdateTenantInput {
  name?: string;
  description?: string;
  isActive?: boolean;
}

/** ユーザー情報（Admin用） */
export interface AdminUser {
  id: string;
  cognitoSub: string;
  email: string;
  familyName: string;
  givenName: string;
  familyNameKana: string | null;
  givenNameKana: string | null;
  isSystemAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

/** ユーザー一覧レスポンス */
export interface AdminUserListResponse {
  items: AdminUser[];
  total: number;
}

/** ユーザー所属テナント情報 */
export interface AdminUserTenant {
  tenantId: string;
  tenantName: string;
  role: string;
}

/** ユーザー詳細レスポンス */
export interface AdminUserDetailResponse {
  id: string;
  cognitoSub: string;
  email: string;
  familyName: string;
  givenName: string;
  familyNameKana: string | null;
  givenNameKana: string | null;
  isSystemAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  tenants: AdminUserTenant[];
}

/** ユーザー更新リクエスト */
export interface UpdateUserInput {
  familyName?: string;
  givenName?: string;
  familyNameKana?: string;
  givenNameKana?: string;
}

/** システム管理者権限更新リクエスト */
export interface UpdateSystemAdminInput {
  isSystemAdmin: boolean;
}

// =====================================
// Generic fetch helper for admin endpoints
// =====================================

/**
 * 認証トークンを取得する
 * @return {Promise<string | null>} 認証トークン、取得できない場合はnull
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * Admin API呼び出しヘルパー関数
 * @param {string} method HTTPメソッド
 * @param {string} path APIパス
 * @param {unknown} body リクエストボディ
 * @return {Promise<T>} APIレスポンス
 */
async function fetchAdminApi<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Get auth token
  const token = await getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = {
    method,
    headers,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${REST_API_BASE_URL}${path}`, options);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { detail?: string }).detail ||
        `API error: ${res.status} ${res.statusText}`,
    );
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return {} as T;
  }

  return res.json();
}

// =====================================
// useAdminTenantsApi - テナント一覧取得
// =====================================

/** useAdminTenantsApiの戻り値 */
export interface UseAdminTenantsApiResult {
  data: AdminTenantListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminTenantListResponse>;
}

/**
 * テナント一覧を取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminTenantsApiResult} テナント一覧取得結果
 */
export function useAdminTenantsApi(): UseAdminTenantsApiResult {
  const [data, setData] = useState<AdminTenantListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchTenants =
    useCallback(async (): Promise<AdminTenantListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenantListResponse>(
          "GET",
          "/api/admin/tenants",
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchTenants().catch(() => {});
  }, [fetchTenants]);

  return {
    data,
    error,
    loading,
    refetch: fetchTenants,
  };
}

// =====================================
// useAdminTenantApi - テナント詳細取得
// =====================================

/** useAdminTenantApiのオプション */
export interface UseAdminTenantApiOptions {
  id: string;
  skip?: boolean;
}

/** useAdminTenantApiの戻り値 */
export interface UseAdminTenantApiResult {
  data: AdminTenantDetailResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminTenantDetailResponse>;
}

/**
 * テナント詳細を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminTenantApiOptions} options オプション
 * @return {UseAdminTenantApiResult} テナント詳細取得結果
 */
export function useAdminTenantApi(
  options: UseAdminTenantApiOptions,
): UseAdminTenantApiResult {
  const [data, setData] = useState<AdminTenantDetailResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchTenant =
    useCallback(async (): Promise<AdminTenantDetailResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenantDetailResponse>(
          "GET",
          `/api/admin/tenants/${options.id}`,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchTenant().catch(() => {});
    }
  }, [options.skip, options.id, fetchTenant]);

  return {
    data,
    error,
    loading,
    refetch: fetchTenant,
  };
}

// =====================================
// useUpdateAdminTenantApi - テナント更新
// =====================================

/** useUpdateAdminTenantApiの戻り値 */
export interface UseUpdateAdminTenantApiResult {
  updateTenant: (id: string, input: UpdateTenantInput) => Promise<AdminTenant>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminTenantApiResult} テナント更新機能
 */
export function useUpdateAdminTenantApi(): UseUpdateAdminTenantApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateTenant = useCallback(
    async (id: string, input: UpdateTenantInput): Promise<AdminTenant> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenant>(
          "PUT",
          `/api/admin/tenants/${id}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateTenant,
    loading,
    error,
  };
}

// =====================================
// useDeleteAdminTenantApi - テナント削除
// =====================================

/** useDeleteAdminTenantApiの戻り値 */
export interface UseDeleteAdminTenantApiResult {
  deleteTenant: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを削除するhook (SYSTEM_ADMIN only)
 * @return {UseDeleteAdminTenantApiResult} テナント削除機能
 */
export function useDeleteAdminTenantApi(): UseDeleteAdminTenantApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteTenant = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchAdminApi<void>("DELETE", `/api/admin/tenants/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    deleteTenant,
    loading,
    error,
  };
}

// =====================================
// useToggleTenantLicenseApi - テナントライセンス切替
// =====================================

/** ライセンス切替レスポンス */
interface TenantLicensesResponse {
  licenses: string[];
}

/** useToggleTenantLicenseApiの戻り値 */
export interface UseToggleTenantLicenseApiResult {
  enableLicense: (tenantId: string, licenseKey: string) => Promise<string[]>;
  disableLicense: (tenantId: string, licenseKey: string) => Promise<string[]>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントライセンスを切り替えるhook (SYSTEM_ADMIN only)
 * @return {UseToggleTenantLicenseApiResult} ライセンス切替機能
 */
export function useToggleTenantLicenseApi(): UseToggleTenantLicenseApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const enableLicense = useCallback(
    async (tenantId: string, licenseKey: string): Promise<string[]> => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await fetchAdminApi<TenantLicensesResponse>(
          "PUT",
          `/api/admin/tenants/${tenantId}/licenses/${licenseKey}`,
        );
        return res.licenses;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const disableLicense = useCallback(
    async (tenantId: string, licenseKey: string): Promise<string[]> => {
      setLoading(true);
      setError(undefined);
      try {
        const res = await fetchAdminApi<TenantLicensesResponse>(
          "DELETE",
          `/api/admin/tenants/${tenantId}/licenses/${licenseKey}`,
        );
        return res.licenses;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    enableLicense,
    disableLicense,
    loading,
    error,
  };
}

// =====================================
// useAdminUsersApi - ユーザー一覧取得
// =====================================

/** useAdminUsersApiのオプション */
export interface UseAdminUsersApiOptions {
  tenantId?: string;
}

/** useAdminUsersApiの戻り値 */
export interface UseAdminUsersApiResult {
  data: AdminUserListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminUserListResponse>;
}

/**
 * ユーザー一覧を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminUsersApiOptions} options オプション
 * @return {UseAdminUsersApiResult} ユーザー一覧取得結果
 */
export function useAdminUsersApi(
  options: UseAdminUsersApiOptions = {},
): UseAdminUsersApiResult {
  const [data, setData] = useState<AdminUserListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async (): Promise<AdminUserListResponse> => {
    setLoading(true);
    setError(undefined);

    try {
      const path = options.tenantId
        ? `/api/admin/users?tenant_id=${options.tenantId}`
        : "/api/admin/users";
      const responseData = await fetchAdminApi<AdminUserListResponse>(
        "GET",
        path,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.tenantId]);

  useEffect(() => {
    fetchUsers().catch(() => {});
  }, [fetchUsers]);

  return {
    data,
    error,
    loading,
    refetch: fetchUsers,
  };
}

// =====================================
// useAdminUserApi - ユーザー詳細取得
// =====================================

/** useAdminUserApiのオプション */
export interface UseAdminUserApiOptions {
  id: string;
  skip?: boolean;
}

/** useAdminUserApiの戻り値 */
export interface UseAdminUserApiResult {
  data: AdminUserDetailResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminUserDetailResponse>;
}

/**
 * ユーザー詳細を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminUserApiOptions} options オプション
 * @return {UseAdminUserApiResult} ユーザー詳細取得結果
 */
export function useAdminUserApi(
  options: UseAdminUserApiOptions,
): UseAdminUserApiResult {
  const [data, setData] = useState<AdminUserDetailResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchUser = useCallback(async (): Promise<AdminUserDetailResponse> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await fetchAdminApi<AdminUserDetailResponse>(
        "GET",
        `/api/admin/users/${options.id}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchUser().catch(() => {});
    }
  }, [options.skip, options.id, fetchUser]);

  return {
    data,
    error,
    loading,
    refetch: fetchUser,
  };
}

// =====================================
// useUpdateAdminUserApi - ユーザー更新
// =====================================

/** useUpdateAdminUserApiの戻り値 */
export interface UseUpdateAdminUserApiResult {
  updateUser: (id: string, input: UpdateUserInput) => Promise<AdminUser>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * ユーザーを更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminUserApiResult} ユーザー更新機能
 */
export function useUpdateAdminUserApi(): UseUpdateAdminUserApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateUser = useCallback(
    async (id: string, input: UpdateUserInput): Promise<AdminUser> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminUser>(
          "PUT",
          `/api/admin/users/${id}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateUser,
    loading,
    error,
  };
}

// =====================================
// useToggleSystemAdminApi - システム管理者権限変更
// =====================================

/** useToggleSystemAdminApiの戻り値 */
export interface UseToggleSystemAdminApiResult {
  toggleSystemAdmin: (id: string, isSystemAdmin: boolean) => Promise<AdminUser>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * システム管理者権限を変更するhook (SYSTEM_ADMIN only)
 * @return {UseToggleSystemAdminApiResult} システム管理者権限変更機能
 */
export function useToggleSystemAdminApi(): UseToggleSystemAdminApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const toggleSystemAdmin = useCallback(
    async (id: string, isSystemAdmin: boolean): Promise<AdminUser> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminUser>(
          "PUT",
          `/api/admin/users/${id}/system-admin`,
          { isSystemAdmin },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    toggleSystemAdmin,
    loading,
    error,
  };
}

// =====================================
// useAdminChangeMemberRole - テナントメンバーロール変更
// =====================================

/** useAdminChangeMemberRoleの戻り値 */
export type UseAdminChangeMemberRoleResult = {
  changeMemberRole: (
    tenantId: string,
    userId: string,
    role: string,
  ) => Promise<AdminTenantMember>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * テナントメンバーのロールを変更するhook (SYSTEM_ADMIN only)
 * @return {UseAdminChangeMemberRoleResult} ロール変更機能
 */
export function useAdminChangeMemberRole(): UseAdminChangeMemberRoleResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const changeMemberRole = useCallback(
    async (
      tenantId: string,
      userId: string,
      role: string,
    ): Promise<AdminTenantMember> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminTenantMember>(
          "PUT",
          `/api/admin/tenants/${tenantId}/members/${userId}/role`,
          { role },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    changeMemberRole,
    loading,
    error,
  };
}

// =====================================
// Admin Feature Flag Types
// =====================================

/** テナント override 情報 */
export interface AdminTenantOverride {
  id: string;
  tenantId: string;
  tenantName: string;
  enabled: boolean;
}

/** Feature Flag 情報（Admin用） */
export interface AdminFeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string;
  enabled: boolean;
  overrides: AdminTenantOverride[];
  createdAt: string;
  updatedAt: string;
}

/** Feature Flag 一覧レスポンス */
export interface AdminFeatureFlagListResponse {
  items: AdminFeatureFlag[];
  total: number;
}

// =====================================
// useAdminFeatureFlagsApi - Feature Flag 一覧取得
// =====================================

/** useAdminFeatureFlagsApiの戻り値 */
export interface UseAdminFeatureFlagsApiResult {
  data: AdminFeatureFlagListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminFeatureFlagListResponse>;
}

/**
 * Feature Flag 一覧を取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminFeatureFlagsApiResult} Feature Flag 一覧取得結果
 */
export function useAdminFeatureFlagsApi(): UseAdminFeatureFlagsApiResult {
  const [data, setData] = useState<AdminFeatureFlagListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchFlags =
    useCallback(async (): Promise<AdminFeatureFlagListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlagListResponse>(
          "GET",
          "/api/admin/feature-flags",
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchFlags().catch(() => {});
  }, [fetchFlags]);

  return {
    data,
    error,
    loading,
    refetch: fetchFlags,
  };
}

// =====================================
// useUpdateAdminFeatureFlagApi - Feature Flag 更新
// =====================================

/** useUpdateAdminFeatureFlagApiの戻り値 */
export interface UseUpdateAdminFeatureFlagApiResult {
  updateFlag: (id: string, enabled: boolean) => Promise<AdminFeatureFlag>;
  setTenantOverride: (
    flagId: string,
    tenantId: string,
    enabled: boolean,
  ) => Promise<AdminFeatureFlag>;
  deleteTenantOverride: (
    flagId: string,
    tenantId: string,
  ) => Promise<AdminFeatureFlag>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Feature Flag を更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminFeatureFlagApiResult} Feature Flag 更新機能
 */
export function useUpdateAdminFeatureFlagApi(): UseUpdateAdminFeatureFlagApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateFlag = useCallback(
    async (id: string, enabled: boolean): Promise<AdminFeatureFlag> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlag>(
          "PUT",
          `/api/admin/feature-flags/${id}`,
          { enabled },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const setTenantOverride = useCallback(
    async (
      flagId: string,
      tenantId: string,
      enabled: boolean,
    ): Promise<AdminFeatureFlag> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlag>(
          "PUT",
          `/api/admin/feature-flags/${flagId}/tenants/${tenantId}`,
          { enabled },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteTenantOverride = useCallback(
    async (flagId: string, tenantId: string): Promise<AdminFeatureFlag> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminFeatureFlag>(
          "DELETE",
          `/api/admin/feature-flags/${flagId}/tenants/${tenantId}`,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateFlag,
    setTenantOverride,
    deleteTenantOverride,
    loading,
    error,
  };
}

// =====================================
// Admin Skills Master Types
// =====================================

/** スキルマスター情報（Admin用） */
export interface AdminSkill {
  id: string;
  normalizedName: string;
  displayName: string;
  category: string | null;
  iconUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/** スキルマスター一覧レスポンス */
export interface AdminSkillListResponse {
  totalCount: number;
  nodes: AdminSkill[];
}

/** スキルマスター更新リクエスト */
export interface UpdateSkillInput {
  displayName?: string;
  category?: string | null;
}

// =====================================
// useAdminSkillsApi - スキルマスター一覧取得
// =====================================

/** useAdminSkillsApiのオプション */
export interface UseAdminSkillsApiOptions {
  q?: string;
  category?: string;
  offset?: number;
  limit?: number;
}

/** useAdminSkillsApiの戻り値 */
export interface UseAdminSkillsApiResult {
  data: AdminSkillListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: (
    options?: UseAdminSkillsApiOptions,
  ) => Promise<AdminSkillListResponse>;
}

/**
 * スキルマスター一覧を取得するhook (SYSTEM_ADMIN only)
 * @param {UseAdminSkillsApiOptions} options オプション
 * @return {UseAdminSkillsApiResult} スキルマスター一覧取得結果
 */
export function useAdminSkillsApi(
  options: UseAdminSkillsApiOptions = {},
): UseAdminSkillsApiResult {
  const [data, setData] = useState<AdminSkillListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Keep latest options in a ref so fetchSkills never depends on them
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const buildPath = useCallback((opts: UseAdminSkillsApiOptions) => {
    const params = new URLSearchParams();
    if (opts.q) params.set("q", opts.q);
    if (opts.category) params.set("category", opts.category);
    if (opts.offset != null) params.set("offset", String(opts.offset));
    if (opts.limit != null) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return qs ? `/api/admin/masters/skills?${qs}` : "/api/admin/masters/skills";
  }, []);

  const fetchSkills = useCallback(
    async (
      fetchOpts?: UseAdminSkillsApiOptions,
    ): Promise<AdminSkillListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminSkillListResponse>(
          "GET",
          buildPath(fetchOpts || optionsRef.current),
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [buildPath],
  );

  // Fetch only once on mount — fetchSkills uses optionsRef so it's stable
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional mount-only fetch
  useEffect(() => {
    fetchSkills().catch(() => {});
  }, []);

  return {
    data,
    error,
    loading,
    refetch: fetchSkills,
  };
}

// =====================================
// useUpdateAdminSkillApi - スキルマスター更新
// =====================================

/** useUpdateAdminSkillApiの戻り値 */
export interface UseUpdateAdminSkillApiResult {
  updateSkill: (id: string, input: UpdateSkillInput) => Promise<AdminSkill>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * スキルマスターを更新するhook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminSkillApiResult} スキルマスター更新機能
 */
export function useUpdateAdminSkillApi(): UseUpdateAdminSkillApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateSkill = useCallback(
    async (id: string, input: UpdateSkillInput): Promise<AdminSkill> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchAdminApi<AdminSkill>(
          "PUT",
          `/api/admin/masters/skills/${id}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateSkill,
    loading,
    error,
  };
}

// =====================================
// useUploadSkillIconApi - アイコンアップロード
// =====================================

/** useUploadSkillIconApiの戻り値 */
export interface UseUploadSkillIconApiResult {
  uploadIcon: (id: string, file: File) => Promise<AdminSkill>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * スキルマスターのアイコンをアップロードするhook (SYSTEM_ADMIN only)
 * @return {UseUploadSkillIconApiResult} アイコンアップロード機能
 */
export function useUploadSkillIconApi(): UseUploadSkillIconApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const uploadIcon = useCallback(
    async (id: string, file: File): Promise<AdminSkill> => {
      setLoading(true);
      setError(undefined);

      try {
        const token = await getAuthToken();
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(
          `${REST_API_BASE_URL}/api/admin/masters/skills/${id}/icon`,
          {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          },
        );

        if (!res.ok) {
          const errorBody = await res.json().catch(() => ({}));
          throw new Error(
            (errorBody as { detail?: string }).detail ||
              `API error: ${res.status} ${res.statusText}`,
          );
        }

        return res.json();
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    uploadIcon,
    loading,
    error,
  };
}

// =====================================
// useDeleteSkillIconApi - アイコン削除
// =====================================

/** useDeleteSkillIconApiの戻り値 */
export interface UseDeleteSkillIconApiResult {
  deleteIcon: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * スキルマスターのアイコンを削除するhook (SYSTEM_ADMIN only)
 * @return {UseDeleteSkillIconApiResult} アイコン削除機能
 */
export function useDeleteSkillIconApi(): UseDeleteSkillIconApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteIcon = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchAdminApi<void>(
        "DELETE",
        `/api/admin/masters/skills/${id}/icon`,
      );
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    deleteIcon,
    loading,
    error,
  };
}

// =====================================
// Admin Usage Types
// =====================================

/** カテゴリ別利用状況 */
export interface AdminCategoryUsage {
  category: string;
  label: string;
  used: number;
  limit: number;
  remaining: number;
  usageRate: number;
  isWarning: boolean;
  unit: string;
}

/** テナント利用概要 */
export interface AdminTenantUsageOverviewItem {
  tenantId: string;
  tenantName: string;
  planName: string;
  status: string;
  categories: AdminCategoryUsage[];
}

/** テナント利用概要一覧レスポンス */
export interface AdminTenantUsageOverviewResponse {
  items: AdminTenantUsageOverviewItem[];
  total: number;
  page: number;
  limit: number;
}

/** LLMトークン統計 */
export interface AdminLLMTokenStats {
  model: string;
  operation: string;
  callCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

/** テナント利用詳細 */
export interface AdminTenantUsageDetailResponse {
  tenantId: string;
  tenantName: string;
  contract: {
    plan: string;
    status: string;
    periodStart: string;
    periodEnd: string;
  } | null;
  categories: AdminCategoryUsage[];
  llmStats: AdminLLMTokenStats[];
}

/** LLMオペレーション統計 */
export interface AdminLLMOperationStats {
  operation: string;
  callCount: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

/** LLMモデル統計 */
export interface AdminLLMModelStats {
  model: string;
  operations: AdminLLMOperationStats[];
}

/** LLM使用量サマリー */
export interface AdminLLMUsageSummaryResponse {
  models: AdminLLMModelStats[];
  totalCostUsd: number;
}

/** マッチング種別統計 */
export interface AdminMatchingTypeStats {
  triggerType: string;
  count: number;
  pct: number;
  avgResults: number;
}

/** マッチング統計 */
export interface AdminMatchingStatsResponse {
  total: number;
  byType: AdminMatchingTypeStats[];
}

/** 契約情報 */
export interface AdminContractResponse {
  id: string;
  planName: string;
  billingCycle: string;
  status: string;
  startDate: string;
  endDate: string;
  createdAt: string;
}

/** 契約作成リクエスト */
export interface CreateContractInput {
  subscriptionPlanId: string;
  billingCycle: string;
  startDate: string;
  endDate: string;
}

/** 契約一覧レスポンス */
export interface AdminContractListResponse {
  items: AdminContractResponse[];
  total: number;
}

// =====================================
// useAdminUsageOverviewApi - 全テナント利用状況
// =====================================

/** useAdminUsageOverviewApiの戻り値 */
export interface UseAdminUsageOverviewApiResult {
  data: AdminTenantUsageOverviewResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminTenantUsageOverviewResponse>;
}

/**
 * 全テナントの利用状況一覧を取得するhook (SYSTEM_ADMIN only)
 * @param {object} options オプション
 * @param {number} [options.page] ページ番号
 * @param {number} [options.limit] 1ページの件数
 * @param {string} [options.plan] プラン名フィルター
 * @return {UseAdminUsageOverviewApiResult} 利用状況一覧取得結果
 */
export function useAdminUsageOverviewApi(
  options: { page?: number; limit?: number; plan?: string } = {},
): UseAdminUsageOverviewApiResult {
  const [data, setData] = useState<
    AdminTenantUsageOverviewResponse | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const { page = 1, limit = 20, plan } = options;

  const fetchData =
    useCallback(async (): Promise<AdminTenantUsageOverviewResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        let path = `/api/admin/usage?page=${page}&limit=${limit}`;
        if (plan) {
          path += `&plan=${encodeURIComponent(plan)}`;
        }
        const result = await fetchAdminApi<AdminTenantUsageOverviewResponse>(
          "GET",
          path,
        );
        if (mountedRef.current) {
          setData(result);
        }
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, [page, limit, plan]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useAdminTenantUsageDetailApi - テナント利用詳細
// =====================================

/** useAdminTenantUsageDetailApiの戻り値 */
export interface UseAdminTenantUsageDetailApiResult {
  data: AdminTenantUsageDetailResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminTenantUsageDetailResponse>;
}

/**
 * テナントの利用詳細を取得するhook (SYSTEM_ADMIN only)
 * @param {object} options オプション
 * @param {string} options.id テナントID
 * @param {boolean} [options.skip] スキップフラグ
 * @return {UseAdminTenantUsageDetailApiResult} テナント利用詳細取得結果
 */
export function useAdminTenantUsageDetailApi(options: {
  id: string;
  skip?: boolean;
}): UseAdminTenantUsageDetailApiResult {
  const [data, setData] = useState<AdminTenantUsageDetailResponse | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(!options.skip);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData =
    useCallback(async (): Promise<AdminTenantUsageDetailResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await fetchAdminApi<AdminTenantUsageDetailResponse>(
          "GET",
          `/api/admin/usage/tenants/${options.id}`,
        );
        if (mountedRef.current) {
          setData(result);
        }
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, [options.id]);

  useEffect(() => {
    mountedRef.current = true;
    if (!options.skip) {
      fetchData().catch(() => undefined);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, options.skip]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useAdminLLMSummaryApi - LLMサマリー
// =====================================

/** useAdminLLMSummaryApiの戻り値 */
export interface UseAdminLLMSummaryApiResult {
  data: AdminLLMUsageSummaryResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminLLMUsageSummaryResponse>;
}

/**
 * LLM使用量サマリーを取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminLLMSummaryApiResult} LLMサマリー取得結果
 */
export function useAdminLLMSummaryApi(): UseAdminLLMSummaryApiResult {
  const [data, setData] = useState<AdminLLMUsageSummaryResponse | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData =
    useCallback(async (): Promise<AdminLLMUsageSummaryResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await fetchAdminApi<AdminLLMUsageSummaryResponse>(
          "GET",
          "/api/admin/usage/llm/summary",
        );
        if (mountedRef.current) {
          setData(result);
        }
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useAdminMatchingStatsApi - マッチング統計
// =====================================

/** useAdminMatchingStatsApiの戻り値 */
export interface UseAdminMatchingStatsApiResult {
  data: AdminMatchingStatsResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminMatchingStatsResponse>;
}

/**
 * マッチング統計を取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminMatchingStatsApiResult} マッチング統計取得結果
 */
export function useAdminMatchingStatsApi(): UseAdminMatchingStatsApiResult {
  const [data, setData] = useState<AdminMatchingStatsResponse | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData =
    useCallback(async (): Promise<AdminMatchingStatsResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await fetchAdminApi<AdminMatchingStatsResponse>(
          "GET",
          "/api/admin/usage/matching/stats",
        );
        if (mountedRef.current) {
          setData(result);
        }
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useAdminContractsApi - 契約一覧
// =====================================

/** useAdminContractsApiの戻り値 */
export interface UseAdminContractsApiResult {
  data: AdminContractListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminContractListResponse>;
}

/**
 * テナントの契約一覧を取得するhook (SYSTEM_ADMIN only)
 * @param {object} options オプション
 * @param {string} options.tenantId テナントID
 * @param {boolean} [options.skip] スキップフラグ
 * @return {UseAdminContractsApiResult} 契約一覧取得結果
 */
export function useAdminContractsApi(options: {
  tenantId: string;
  skip?: boolean;
}): UseAdminContractsApiResult {
  const [data, setData] = useState<AdminContractListResponse | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(!options.skip);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData =
    useCallback(async (): Promise<AdminContractListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await fetchAdminApi<AdminContractListResponse>(
          "GET",
          `/api/admin/contracts/${options.tenantId}/contracts`,
        );
        if (mountedRef.current) {
          setData(result);
        }
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }, [options.tenantId]);

  useEffect(() => {
    mountedRef.current = true;
    if (!options.skip) {
      fetchData().catch(() => undefined);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, options.skip]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useCreateAdminContractApi - 契約作成
// =====================================

/** useCreateAdminContractApiの戻り値 */
export interface UseCreateAdminContractApiResult {
  createContract: (
    tenantId: string,
    input: CreateContractInput,
  ) => Promise<AdminContractResponse>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 契約を作成するhook (SYSTEM_ADMIN only)
 * @return {UseCreateAdminContractApiResult} 契約作成機能
 */
export function useCreateAdminContractApi(): UseCreateAdminContractApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createContract = useCallback(
    async (
      tenantId: string,
      input: CreateContractInput,
    ): Promise<AdminContractResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchAdminApi<AdminContractResponse>(
          "POST",
          `/api/admin/contracts/${tenantId}/contracts`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createContract, loading, error };
}

// =====================================
// useCancelAdminContractApi - 契約キャンセル
// =====================================

/** useCancelAdminContractApiの戻り値 */
export interface UseCancelAdminContractApiResult {
  cancelContract: (
    tenantId: string,
    contractId: string,
  ) => Promise<AdminContractResponse>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 契約をキャンセルするhook (SYSTEM_ADMIN only)
 * @return {UseCancelAdminContractApiResult} 契約キャンセル機能
 */
export function useCancelAdminContractApi(): UseCancelAdminContractApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const cancelContract = useCallback(
    async (
      tenantId: string,
      contractId: string,
    ): Promise<AdminContractResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        return await fetchAdminApi<AdminContractResponse>(
          "PUT",
          `/api/admin/contracts/${tenantId}/contracts/${contractId}`,
          { status: "cancelled" },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { cancelContract, loading, error };
}

// =====================================
// Admin Subscription Plan Types
// =====================================

/** サブスクリプションプラン */
export interface AdminSubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
}

/** プラン一覧レスポンス */
export interface AdminSubscriptionPlanListResponse {
  items: AdminSubscriptionPlan[];
}

// =====================================
// useAdminSubscriptionPlansApi - プラン一覧
// =====================================

/** useAdminSubscriptionPlansApiの戻り値 */
export interface UseAdminSubscriptionPlansApiResult {
  data: AdminSubscriptionPlanListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
}

/**
 * サブスクリプションプラン一覧を取得するhook (SYSTEM_ADMIN only)
 * @return {UseAdminSubscriptionPlansApiResult} プラン一覧取得結果
 */
export function useAdminSubscriptionPlansApi(): UseAdminSubscriptionPlansApiResult {
  const [data, setData] = useState<
    AdminSubscriptionPlanListResponse | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const result = await fetchAdminApi<AdminSubscriptionPlanListResponse>(
          "GET",
          "/api/admin/contracts/subscription-plans",
        );
        if (mountedRef.current) {
          setData(result);
        }
      } catch (e) {
        if (mountedRef.current) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    })();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { data, loading, error };
}

// =====================================
// Admin Plan Master Types (ISSUE-762)
// =====================================

/** プラン × カテゴリ単位の上限 */
export type AdminPlanLimit = {
  category: string;
  label: string;
  monthlyLimit: number;
  overageLimit: number;
  /** Decimal を表現する文字列 (例: "10.50") */
  overageUnitPrice: string;
  warningThresholdPct: number;
};

/** プラン詳細（管理者向け） */
export type AdminPlan = {
  id: string;
  name: string;
  displayName: string;
  limits: AdminPlanLimit[];
  createdAt: string;
  updatedAt: string;
};

/** プラン一覧レスポンス */
export type AdminPlanListResponse = {
  items: AdminPlan[];
};

/** プラン上限 upsert 入力 */
export type UpsertPlanLimitInput = {
  monthlyLimit: number;
  overageLimit: number;
  overageUnitPrice: string;
  warningThresholdPct: number;
};

// =====================================
// useAdminPlansApi - プラン一覧（上限つき）
// =====================================

/** useAdminPlansApi の戻り値 */
export type UseAdminPlansApiResult = {
  data: AdminPlanListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminPlanListResponse>;
};

/**
 * プランマスター一覧（カテゴリ別上限を含む）を取得する hook (SYSTEM_ADMIN only)
 * @return {UseAdminPlansApiResult} プラン一覧取得結果
 */
export function useAdminPlansApi(): UseAdminPlansApiResult {
  const [data, setData] = useState<AdminPlanListResponse | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (): Promise<AdminPlanListResponse> => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await fetchAdminApi<AdminPlanListResponse>(
        "GET",
        "/api/admin/plans",
      );
      if (mountedRef.current) {
        setData(result);
      }
      return result;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (mountedRef.current) {
        setError(err);
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// =====================================
// useUpdateAdminPlanApi - プラン display_name 更新
// =====================================

/** useUpdateAdminPlanApi の戻り値 */
export type UseUpdateAdminPlanApiResult = {
  updatePlan: (planId: string, displayName: string) => Promise<AdminPlan>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * プランの表示名を更新する hook (SYSTEM_ADMIN only)
 * @return {UseUpdateAdminPlanApiResult} 更新機能
 */
export function useUpdateAdminPlanApi(): UseUpdateAdminPlanApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updatePlan = useCallback(
    async (planId: string, displayName: string): Promise<AdminPlan> => {
      setLoading(true);
      setError(undefined);
      try {
        return await fetchAdminApi<AdminPlan>(
          "PATCH",
          `/api/admin/plans/${planId}`,
          { displayName },
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updatePlan, loading, error };
}

// =====================================
// useCreateAdminPlanApi - プラン新規作成
// =====================================

/** useCreateAdminPlanApi の戻り値 */
export type UseCreateAdminPlanApiResult = {
  createPlan: (input: {
    name: string;
    displayName: string;
  }) => Promise<AdminPlan>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * プランを新規作成する hook (SYSTEM_ADMIN only)
 * @return {UseCreateAdminPlanApiResult} 作成機能
 */
export function useCreateAdminPlanApi(): UseCreateAdminPlanApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createPlan = useCallback(
    async (input: {
      name: string;
      displayName: string;
    }): Promise<AdminPlan> => {
      setLoading(true);
      setError(undefined);
      try {
        return await fetchAdminApi<AdminPlan>(
          "POST",
          "/api/admin/plans",
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createPlan, loading, error };
}

// =====================================
// useUpsertAdminPlanLimitApi - プラン上限 upsert
// =====================================

/** useUpsertAdminPlanLimitApi の戻り値 */
export type UseUpsertAdminPlanLimitApiResult = {
  upsertLimit: (
    planId: string,
    category: string,
    input: UpsertPlanLimitInput,
  ) => Promise<AdminPlan>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * プラン × カテゴリの上限を upsert する hook (SYSTEM_ADMIN only)
 * @return {UseUpsertAdminPlanLimitApiResult} upsert 機能
 */
export function useUpsertAdminPlanLimitApi(): UseUpsertAdminPlanLimitApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const upsertLimit = useCallback(
    async (
      planId: string,
      category: string,
      input: UpsertPlanLimitInput,
    ): Promise<AdminPlan> => {
      setLoading(true);
      setError(undefined);
      try {
        return await fetchAdminApi<AdminPlan>(
          "PUT",
          `/api/admin/plans/${planId}/limits/${category}`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { upsertLimit, loading, error };
}

// =====================================
// Admin Matching Feedback Types (ISSUE-829)
// =====================================

/** マッチング評価一覧のテナント参照 */
export type AdminFeedbackTenantRef = {
  id: string;
  name: string;
};

/** マッチング評価一覧のワークスペース参照 */
export type AdminFeedbackWorkspaceRef = {
  id: string;
  name: string;
};

/** マッチング評価一覧の評価者参照 */
export type AdminFeedbackUserRef = {
  id: string;
  email: string;
  familyName: string;
  givenName: string;
};

/** マッチング評価一覧の関連マッチング結果 */
export type AdminFeedbackResultRef = {
  id: string;
  sourceType: string;
  sourceEmailId: string;
  sourceSubject: string | null;
  matchedEmailId: string | null;
  matchedSubject: string;
  totalScore: number;
  status: string;
};

/** マッチング評価一覧の1行 */
export type AdminMatchingFeedbackItem = {
  id: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
  tenant: AdminFeedbackTenantRef;
  workspace: AdminFeedbackWorkspaceRef;
  createdBy: AdminFeedbackUserRef | null;
  result: AdminFeedbackResultRef;
};

/** マッチング評価一覧レスポンス */
export type AdminMatchingFeedbackListResponse = {
  items: AdminMatchingFeedbackItem[];
  total: number;
  page: number;
  limit: number;
};

/** マッチング評価一覧の絞り込みオプション */
export type UseAdminMatchingFeedbacksApiOptions = {
  tenantId?: string;
  ratings?: number[];
  createdAtFrom?: string;
  createdAtTo?: string;
  commentQuery?: string;
  sort?: "created_at" | "rating" | "total_score";
  order?: "asc" | "desc";
  page?: number;
  limit?: number;
};

/** useAdminMatchingFeedbacksApi の戻り値 */
export type UseAdminMatchingFeedbacksApiResult = {
  data: AdminMatchingFeedbackListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<AdminMatchingFeedbackListResponse>;
};

/**
 * 全テナント横断のマッチング評価一覧を取得する hook (SYSTEM_ADMIN only)
 * @param {object} options 絞り込み・並び替え・ページングのオプション
 * @param {string} [options.tenantId] テナントID絞り込み
 * @param {number[]} [options.ratings] ★評価の絞り込み (複数)
 * @param {string} [options.createdAtFrom] 評価日時 開始 (YYYY-MM-DD)
 * @param {string} [options.createdAtTo] 評価日時 終了 (YYYY-MM-DD)
 * @param {string} [options.commentQuery] コメント検索キーワード
 * @param {string} [options.sort] ソートキー
 * @param {string} [options.order] ソート順
 * @param {number} [options.page] ページ番号 (1始まり)
 * @param {number} [options.limit] 1ページの件数
 * @return {UseAdminMatchingFeedbacksApiResult} 一覧取得結果
 */
export function useAdminMatchingFeedbacksApi(
  options: UseAdminMatchingFeedbacksApiOptions = {},
): UseAdminMatchingFeedbacksApiResult {
  const [data, setData] = useState<
    AdminMatchingFeedbackListResponse | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const mountedRef = useRef(true);
  // 直近で発火した fetch の世代番号。古い fetch の解決を破棄するために使う。
  const requestIdRef = useRef(0);

  const {
    tenantId,
    ratings,
    createdAtFrom,
    createdAtTo,
    commentQuery,
    sort,
    order,
    page = 1,
    limit = 20,
  } = options;

  // ratings は array なので参照同一性が壊れる。同値判定は文字列化したキーで行う。
  const ratingsKey = ratings ? ratings.join(",") : "";

  const fetchData =
    useCallback(async (): Promise<AdminMatchingFeedbackListResponse> => {
      const myId = ++requestIdRef.current;
      setLoading(true);
      setError(undefined);

      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));
        if (tenantId) params.set("tenant_id", tenantId);
        if (createdAtFrom) params.set("created_at_from", createdAtFrom);
        if (createdAtTo) params.set("created_at_to", createdAtTo);
        if (commentQuery) params.set("comment_query", commentQuery);
        if (sort) params.set("sort", sort);
        if (order) params.set("order", order);
        for (const r of ratingsKey ? ratingsKey.split(",").map(Number) : []) {
          params.append("rating", String(r));
        }

        const result = await fetchAdminApi<AdminMatchingFeedbackListResponse>(
          "GET",
          `/api/admin/matching-feedbacks?${params.toString()}`,
        );
        // 自分より新しい fetch が走っていれば結果を破棄
        if (mountedRef.current && requestIdRef.current === myId) {
          setData(result);
        }
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        if (mountedRef.current && requestIdRef.current === myId) {
          setError(err);
        }
        throw err;
      } finally {
        if (mountedRef.current && requestIdRef.current === myId) {
          setLoading(false);
        }
      }
    }, [
      tenantId,
      ratingsKey,
      createdAtFrom,
      createdAtTo,
      commentQuery,
      sort,
      order,
      page,
      limit,
    ]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
